/**
 * Browser mirror of the backend telemetry URL scrubbers (server/lib/phi-redact.ts).
 *
 * The deny-list constant set is shared from shared/telemetry-constants.ts so the two tiers can never
 * diverge (B-L3); the URL/scrub LOGIC is kept as a tiny, independently-tested copy here rather than a
 * cross-import of the server module, so the client bundle does not couple to the server build graph.
 * Used by client/logger-init.ts to sanitize auto-collected AJAX dependencies + page views before any
 * telemetry leaves the browser (HIPAA defense-in-depth — the browser App Insights SDK can auto-track
 * fetch/XHR with the full target URL incl. query string).
 *
 * Like the server module, sanitizeTelemetryItem FAILS CLOSED: on an unexpected shape / throw it strips
 * the risky payload (url/uri/target/name/data/properties) instead of exporting it raw, and console.warns.
 */
import { DENY_PROP_KEYS } from "../../shared/telemetry-constants";

export { DENY_PROP_KEYS };

/** Drop everything from the first `?` or `#` — removes query strings / fragments that may carry PHI. */
export function stripQuery(s: string): string {
  if (typeof s !== "string") return s;
  const cut = s.search(/[?#]/);
  return cut === -1 ? s : s.slice(0, cut);
}

function stripMatrix(path: string): string {
  const semi = path.indexOf(";");
  return semi === -1 ? path : path.slice(0, semi);
}

/** PHI-safe URL: scheme+host+path only (query/fragment/matrix params removed). Never throws. */
export function sanitizeUrl(u: string): string {
  if (typeof u !== "string") return u;
  try {
    const parsed = new URL(u);
    return `${parsed.protocol}//${parsed.host}${stripMatrix(parsed.pathname)}`;
  } catch {
    return stripMatrix(stripQuery(u));
  }
}

/** Minimal shape of an App Insights web SDK telemetry item (only the fields we sanitize). */
interface TelemetryItem {
  baseData?: {
    uri?: string;
    url?: string;
    target?: string;
    name?: string;
    data?: string;
    properties?: Record<string, unknown>;
    [k: string]: unknown;
  };
  data?: Record<string, unknown>;
  [k: string]: unknown;
}

function scrubProps(props: Record<string, unknown> | undefined): void {
  if (!props || typeof props !== "object") return;
  for (const k of Object.keys(props)) {
    if (DENY_PROP_KEYS.has(k.toLowerCase())) delete props[k];
  }
}

/** FAIL-CLOSED last resort: blank every field that could carry PHI rather than exporting it raw. */
function stripItemUnsafe(item: unknown): void {
  try {
    const it = item as TelemetryItem;
    const b = it?.baseData;
    if (b) {
      if (typeof b.uri === "string") b.uri = "";
      if (typeof b.url === "string") b.url = "";
      if (typeof b.target === "string") b.target = "";
      if (typeof b.name === "string") b.name = "";
      if (typeof b.data === "string") b.data = "";
      if (b.properties && typeof b.properties === "object") {
        for (const k of Object.keys(b.properties)) delete b.properties[k];
      }
    }
    if (it?.data && typeof it.data === "object") {
      for (const k of Object.keys(it.data)) delete it.data[k];
    }
  } catch {
    /* truly last-resort */
  }
}

/**
 * addTelemetryInitializer callback: strip query strings / PHI from auto-collected page views and AJAX
 * dependencies, and drop deny-listed custom properties. Mutates in place; ALWAYS returns `true` (keep
 * the now-sanitized item). FAILS CLOSED — on a throw it strips the risky payload AND console.warns (so a
 * web-SDK envelope-schema regression is observable) rather than keeping the raw item (C1/B-M1).
 */
export function sanitizeTelemetryItem(item: unknown): boolean {
  try {
    const it = item as TelemetryItem;
    const b = it?.baseData;
    if (b) {
      if (typeof b.uri === "string") b.uri = sanitizeUrl(b.uri);
      if (typeof b.url === "string") b.url = sanitizeUrl(b.url);
      if (typeof b.target === "string") b.target = sanitizeUrl(b.target);
      if (typeof b.name === "string") b.name = stripQuery(b.name);
      if (typeof b.data === "string") b.data = sanitizeUrl(b.data);
      scrubProps(b.properties);
    }
    // Custom dimensions on the item itself (web SDK stores them under `data`).
    scrubProps(it?.data);
  } catch (err) {
    // FAIL CLOSED — never keep raw. Strip the risky payload then surface the schema regression.
    stripItemUnsafe(item);
    // eslint-disable-next-line no-console
    console.warn("[phi-redact] sanitizeTelemetryItem failed - stripped item fail-closed:", err);
  }
  return true;
}
